import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Upload, FileCheck2, Plus } from "lucide-react";

export default function Imports() {
  const [providers, setProviders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [fileB64, setFileB64] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("csv");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [batchRows, setBatchRows] = useState(null);

  const load = () => { api.get("/imports/providers").then((r) => setProviders(r.data)); api.get("/imports/batches").then((r) => setBatches(r.data)); };
  useEffect(() => { load(); }, []);

  const provider = providers.find((p) => p.id === providerId);

  const onFile = (file) => {
    setFileName(file.name);
    setFileType(file.name.endsWith(".xlsx") ? "xlsx" : "csv");
    const reader = new FileReader();
    reader.onload = () => setFileB64(reader.result);
    reader.readAsDataURL(file);
  };

  const doPreview = async () => {
    try {
      const r = await api.post("/imports/preview", { file_base64: fileB64, file_type: fileType });
      setPreview(r.data);
      if (provider) setMapping({ ...provider.mapping });
      toast.success(`Parsed ${r.data.total_rows} rows`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doCommit = async () => {
    try {
      const r = await api.post("/imports/commit", { file_base64: fileB64, file_type: fileType, provider_id: providerId, mapping, filename: fileName });
      setResult(r.data); toast.success(`${r.data.rows_valid} valid / ${r.data.rows_rejected} rejected`); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadSample = async (pid) => {
    const r = await api.get(`/imports/sample/${pid}`);
    const blob = new Blob([r.data.content], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = r.data.filename; a.click(); URL.revokeObjectURL(url);
  };

  const viewBatch = async (id) => { const r = await api.get(`/imports/batches/${id}`); setBatchRows(r.data); };

  return (
    <div>
      <PageHeader overline="Operations" title="Offshore Data Imports"
        subtitle="Configurable import framework — no fixed limit of AMCs/RTAs. Common data model with provider-specific, versioned mappings." testid="imports-header"
        actions={<AddProviderDialog onAdded={load} />} />

      <Tabs defaultValue="import">
        <TabsList><TabsTrigger value="import" data-testid="tab-import">New Import</TabsTrigger><TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger><TabsTrigger value="samples" data-testid="tab-samples">Sample Files</TabsTrigger></TabsList>

        <TabsContent value="import" className="mt-4 space-y-4">
          <Card className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Provider</Label>
                <Select value={providerId} onValueChange={setProviderId}>
                  <SelectTrigger data-testid="import-provider"><SelectValue placeholder="Select provider" /></SelectTrigger>
                  <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.version})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>File (CSV / XLSX)</Label>
                <Input type="file" accept=".csv,.xlsx" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid="import-file" />
              </div>
            </div>
            <Button onClick={doPreview} disabled={!fileB64 || !providerId} className="gap-1" data-testid="preview-btn"><Upload className="h-4 w-4" /> Preview & map</Button>
          </Card>

          {preview && (
            <Card className="p-5">
              <h3 className="font-semibold mb-3">Column mapping ({preview.total_rows} rows)</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {Object.keys(mapping).map((field) => (
                  <div key={field}>
                    <Label className="text-xs capitalize">{field}</Label>
                    <Select value={mapping[field]} onValueChange={(v) => setMapping({ ...mapping, [field]: v })}>
                      <SelectTrigger className="h-9" data-testid={`map-${field}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border border-border rounded-lg mb-4">
                <Table><TableHeader><TableRow>{preview.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>{preview.rows.map((row, i) => <TableRow key={i}>{preview.columns.map((c) => <TableCell key={c} className="text-xs">{row[c]}</TableCell>)}</TableRow>)}</TableBody>
                </Table>
              </div>
              <Button onClick={doCommit} className="gap-1" data-testid="commit-btn"><FileCheck2 className="h-4 w-4" /> Validate & import</Button>
            </Card>
          )}

          {result && (
            <Card className="p-5" data-testid="import-result">
              <div className="flex gap-4 flex-wrap mb-3">
                <Stat label="Total" v={result.rows_total} /><Stat label="Valid" v={result.rows_valid} cls="text-emerald-600" /><Stat label="Rejected" v={result.rows_rejected} cls="text-red-600" />
                <div><div className="overline-label text-muted-foreground">Batch</div><div className="font-num font-bold">{result.ref}</div></div>
              </div>
              {result.rejected_detail?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Rejected rows</h4>
                  {result.rejected_detail.map((r, i) => <div key={i} className="text-xs text-red-600 mb-1">Row {r.row_number}: {r.reasons.join("; ")}</div>)}
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="p-5">
            {batches.length === 0 ? <EmptyState title="No imports yet" /> : (
              <Table><TableHeader><TableRow><TableHead>Batch</TableHead><TableHead>Provider</TableHead><TableHead>File</TableHead><TableHead>Total</TableHead><TableHead>Valid</TableHead><TableHead>Rejected</TableHead><TableHead>By</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{batches.map((b) => (
                  <TableRow key={b.id} data-testid={`batch-row-${b.ref}`}>
                    <TableCell className="font-num text-xs">{b.ref}</TableCell><TableCell>{b.provider_id}</TableCell><TableCell className="text-xs">{b.filename}</TableCell>
                    <TableCell className="font-num">{b.rows_total}</TableCell><TableCell className="font-num text-emerald-600">{b.rows_valid}</TableCell><TableCell className="font-num text-red-600">{b.rows_rejected}</TableCell>
                    <TableCell className="text-xs">{b.imported_by}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => viewBatch(b.id)}>Rows</Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
            {batchRows && (
              <div className="mt-5 border-t border-border pt-4">
                <h4 className="font-semibold text-sm mb-2">{batchRows.batch?.ref} — traceable rows (source: {batchRows.batch?.filename})</h4>
                <div className="overflow-x-auto">
                  <Table><TableHeader><TableRow><TableHead>Row #</TableHead><TableHead>Folio</TableHead><TableHead>Scheme</TableHead><TableHead>Status</TableHead><TableHead>Reasons</TableHead></TableRow></TableHeader>
                    <TableBody>{batchRows.rows.map((r) => (
                      <TableRow key={r.id}><TableCell className="font-num text-xs">{r.row_number}</TableCell><TableCell className="text-xs">{r.mapped?.folio}</TableCell><TableCell className="text-xs">{r.mapped?.scheme}</TableCell>
                        <TableCell><StatusBadge status={r.status === "valid" ? "clear" : "rejected"} /></TableCell><TableCell className="text-xs text-red-600">{(r.reasons || []).join("; ")}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="samples" className="mt-4">
          <Card className="p-5">
            <p className="text-sm text-muted-foreground mb-4">Downloadable test files with different structures. Each includes some invalid/duplicate rows to demonstrate validation.</p>
            <div className="space-y-2">
              {["camsonline", "kfintech", "offshore_fundhub"].map((pid) => (
                <div key={pid} className="flex items-center justify-between border border-border rounded-lg p-3">
                  <span className="text-sm font-medium">{providers.find((p) => p.id === pid)?.name || pid}</span>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadSample(pid)} data-testid={`download-sample-${pid}`}><Download className="h-4 w-4" /> Download</Button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-amber-700 mt-4">Compatibility with Barjeel's actual formats is not claimed until those files are reviewed and tested. A new format needs a custom adapter only when it cannot be expressed as a simple column mapping (multi-row headers, nested sections, derived fields).</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, v, cls = "" }) { return <div><div className="overline-label text-muted-foreground">{label}</div><div className={`font-num font-bold text-lg ${cls}`}>{v}</div></div>; }

function AddProviderDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ id: "", name: "", file_type: "csv", version: "v1" });
  const [cols, setCols] = useState("folio,scheme,units,nav,value,arn,valuation_date,currency");
  const add = async () => {
    const fields = ["folio", "scheme", "units", "nav", "value", "arn", "valuation_date", "currency"];
    const colArr = cols.split(",").map((c) => c.trim());
    const mapping = {}; fields.forEach((fld, i) => { if (colArr[i]) mapping[fld] = colArr[i]; });
    try { const r = await api.post("/imports/providers", { ...f, mapping }); toast.success(r.data.message); setOpen(false); onAdded(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="gap-1" data-testid="add-provider-btn"><Plus className="h-4 w-4" /> Add Provider</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Provider (via configuration)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Provider ID</Label><Input value={f.id} onChange={(e) => setF({ ...f, id: e.target.value.replace(/\s/g, "_").toLowerCase() })} data-testid="np-id" /></div>
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="np-name" /></div>
          </div>
          <div><Label>Source columns (order: folio, scheme, units, nav, value, arn, valuation_date, currency)</Label><Input value={cols} onChange={(e) => setCols(e.target.value)} data-testid="np-cols" /></div>
          <p className="text-xs text-muted-foreground">This demonstrates adding another provider through configuration — no code deployment required.</p>
        </div>
        <DialogFooter><Button onClick={add} disabled={!f.id || !f.name} data-testid="np-save">Add Provider</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
